import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/layout/Layout";
import Loader from "@/components/layout/Loader";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Machinery from "@/pages/Machinery";
import Services from "@/pages/Services";
import Products from "@/pages/Products";
import Process from "@/pages/Process";
import Inquiry from "@/pages/Inquiry";
import { MACHINES, COMPANY } from "@/lib/site";

function App() {
  const [loading, setLoading] = useState(() => {
    try {
      return !sessionStorage.getItem("pahi_loaded");
    } catch {
      return true;
    }
  });

  const dismiss = useCallback(() => {
    try { sessionStorage.setItem("pahi_loaded", "1"); } catch {}
    setLoading(false);
  }, []);

  // Hard safety net (rAF-based, immune to setTimeout throttling)
  useEffect(() => {
    if (!loading) return;
    let raf, start = null;
    const tick = (ts) => {
      if (!start) start = ts;
      if (ts - start > 3500) dismiss();
      else raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loading, dismiss]);

  // Prefetch all secondary images during idle time -> instant page loads
  useEffect(() => {
    const urls = [
      ...new Set(MACHINES.map((m) => m.image).filter(Boolean)),
      COMPANY.ceo,
    ];
    const prefetch = () => urls.forEach((src) => { const img = new Image(); img.decoding = "async"; img.src = src; });
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const id = ric(prefetch);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  return (
    <HelmetProvider>
    <div className="App">
      {loading && <Loader onDone={dismiss} />}

      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/machinery" element={<Machinery />} />
            <Route path="/services" element={<Services />} />
            <Route path="/products" element={<Products />} />
            <Route path="/process" element={<Process />} />
            <Route path="/inquiry" element={<Inquiry />} />
          </Route>
        </Routes>
      </BrowserRouter>

      <Toaster position="top-center" richColors />
    </div>
    </HelmetProvider>
  );
}

export default App;
